import React, { useEffect, useState } from "react";
import axios from "axios";
import { Trash2, PencilLine, MessageCircle } from "lucide-react";
import CircularProgress from "@mui/material/CircularProgress";
import { toast } from "react-hot-toast";

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

function AdminTestimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    name: "",
    message: "",
    isVerified: false,
  });
  const [editingId, setEditingId] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const testimonialsPerPage = 5;
  const [selectedIds, setSelectedIds] = useState([]);

  const fetchTestimonials = async () => {
    try {
      const res = await axios.get(`${SERVER_URL}/api/testimonials`);
      setTestimonials(res.data);
      setSelectedIds([]);
    } catch (err) {
      toast.error("Couldn't load testimonials. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${SERVER_URL}/api/testimonials/${editingId}`, form);
        toast.success("Testimonial updated");
      } else {
        await axios.post(`${SERVER_URL}/api/testimonials`, form);
        toast.success("Testimonial added");
      }
      setForm({ name: "", message: "", isVerified: false });
      setEditingId(null);
      fetchTestimonials();
    } catch (err) {
      toast.error("Couldn't submit. Please try again.");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this testimonial?")) {
      try {
        await axios.delete(`${SERVER_URL}/api/testimonials/${id}`);
        fetchTestimonials();
        toast.success("Deleted successfully");
      } catch (err) {
        toast.error("Couldn't delete. Please try again.");
      }
    }
  };

  const handleEdit = (testimonial) => {
    setForm({
      name: testimonial.name,
      message: testimonial.message,
      isVerified: testimonial.isVerified,
    });
    setEditingId(testimonial._id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedIds.length} testimonials?`)) return;
    try {
      await Promise.all(
        selectedIds.map((id) =>
          axios.delete(`${SERVER_URL}/api/testimonials/${id}`),
        ),
      );
      setSelectedIds([]);
      fetchTestimonials();
      toast.success("Bulk delete successful");
    } catch (err) {
      toast.error("Couldn't delete selected testimonials. Please try again.");
    }
  };

  const indexOfLast = currentPage * testimonialsPerPage;
  const indexOfFirst = indexOfLast - testimonialsPerPage;
  const currentTestimonials = testimonials.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(testimonials.length / testimonialsPerPage);

  return (
    <div className="w-full max-w-7xl mx-auto space-y-8">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-black text-gray-900 tracking-tight">
          Manage Testimonials
        </h1>
        <p className="text-gray-500 mt-1">
          Review, add, and moderate customer testimonials
        </p>
      </div>

      {/* Form */}
      <div className="bg-white border border-gray-100 rounded-[2rem] shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8">
        <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-3">
          <span className="w-8 h-8 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-lg flex items-center justify-center text-white text-sm">
            {editingId ? "✏" : "+"}
          </span>
          {editingId ? "Edit Testimonial" : "Add New Testimonial"}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1">
            <label className="text-sm font-semibold text-gray-700">Name</label>
            <input
              type="text"
              placeholder="Customer name"
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-semibold text-gray-700">
              Message
            </label>
            <textarea
              placeholder="Customer message"
              rows={4}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition resize-none"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              required
            />
          </div>

          <label className="inline-flex items-center gap-3 cursor-pointer">
            <div className="relative">
              <input
                type="checkbox"
                checked={form.isVerified}
                onChange={(e) =>
                  setForm({ ...form, isVerified: e.target.checked })
                }
                className="sr-only"
              />
              <div
                className={`w-12 h-6 rounded-full transition-colors duration-300 ${form.isVerified ? "bg-yellow-500" : "bg-gray-200"}`}
              >
                <div
                  className={`w-5 h-5 bg-white rounded-full shadow-sm transform transition-transform duration-300 mt-0.5 ${form.isVerified ? "translate-x-6 ml-0.5" : "translate-x-0.5"}`}
                />
              </div>
            </div>
            <span
              className={`font-medium text-sm ${form.isVerified ? "text-yellow-600" : "text-gray-500"}`}
            >
              {form.isVerified ? "Verified" : "Mark as Verified"}
            </span>
          </label>

          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              className="px-8 py-2.5 rounded-xl bg-gradient-to-r from-yellow-500 to-yellow-600 text-white font-bold hover:from-yellow-600 hover:to-yellow-700 shadow-lg shadow-yellow-500/20 transition"
            >
              {editingId ? "Update" : "Add"} Testimonial
            </button>
            {editingId && (
              <button
                type="button"
                onClick={() => {
                  setForm({ name: "", message: "", isVerified: false });
                  setEditingId(null);
                }}
                className="px-6 py-2.5 rounded-xl bg-gray-100 text-gray-700 font-semibold hover:bg-gray-200 transition"
              >
                Cancel Edit
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Bulk Delete */}
      {selectedIds.length > 0 && (
        <div className="flex items-center justify-between bg-red-50 border border-red-100 rounded-2xl px-6 py-3">
          <span className="text-red-700 font-semibold text-sm">
            {selectedIds.length} selected
          </span>
          <button
            onClick={handleBulkDelete}
            className="flex items-center gap-2 bg-red-500 text-white px-4 py-2 rounded-xl hover:bg-red-600 transition text-sm font-semibold"
          >
            <Trash2 size={14} /> Delete Selected
          </button>
        </div>
      )}

      {/* Testimonials List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <CircularProgress size={35} sx={{ color: "#eab308" }} />
        </div>
      ) : testimonials.length === 0 ? (
        <div className="bg-white border border-gray-100 rounded-[2rem] flex flex-col items-center justify-center h-64 gap-3">
          <MessageCircle className="w-12 h-12 text-gray-200" />
          <p className="text-gray-400 font-bold text-sm tracking-tight">
            No testimonials recorded
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {currentTestimonials.map((t) => (
            <div
              key={t._id}
              className={`bg-white border rounded-2xl p-5 shadow-sm hover:shadow-md transition-all duration-300 group ${
                selectedIds.includes(t._id)
                  ? "border-yellow-300 bg-yellow-50/30"
                  : "border-gray-100 font-medium"
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4 flex-1">
                  <div className="mt-1 flex flex-col items-center gap-2">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(t._id)}
                      onChange={() => toggleSelect(t._id)}
                      className="w-4 h-4 accent-yellow-500 rounded cursor-pointer"
                    />
                    <span className="text-[9px] font-black text-gray-300 uppercase tracking-tighter">
                      Select
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 bg-gradient-to-br from-gray-800 to-gray-950 rounded-lg flex items-center justify-center text-yellow-500 text-[10px] font-black group-hover:from-yellow-500 group-hover:to-yellow-600 group-hover:text-white transition-all duration-500">
                        {t.name?.charAt(0)?.toUpperCase()}
                      </div>
                      <div className="flex flex-col">
                        <p className="font-black text-gray-900 text-sm uppercase tracking-tight leading-none">
                          {t.name}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span
                            className={`inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${t.isVerified ? "bg-green-100 text-green-700" : "bg-red-50 text-red-500"}`}
                          >
                            {t.isVerified ? "Verified User" : "Pending Review"}
                          </span>
                        </div>
                      </div>
                    </div>
                    <p className="text-gray-600 text-[13px] leading-relaxed italic border-l-2 border-yellow-100 pl-3 py-1 mt-2">
                      "{t.message}"
                    </p>
                  </div>
                </div>

                <div className="flex flex-col gap-1.5 flex-shrink-0">
                  <button
                    onClick={() => handleEdit(t)}
                    className="p-1.5 rounded-lg bg-gray-50 text-gray-400 hover:text-yellow-600 hover:bg-yellow-50 transition-all"
                    title="Modify"
                  >
                    <PencilLine size={14} />
                  </button>
                  <button
                    onClick={() => handleDelete(t._id)}
                    className="p-1.5 rounded-lg bg-gray-50 text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all"
                    title="Remove"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-3 pb-4">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition font-medium text-sm"
          >
            ← Prev
          </button>
          <span className="text-sm font-semibold text-gray-700 bg-white border border-gray-100 px-4 py-2 rounded-xl">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages}
            className="px-4 py-2 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition font-medium text-sm"
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

export default AdminTestimonials;
